import React from 'react';
import { X, Plus, Minus, ShoppingBag, Trash2, ExternalLink } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';

// Floating Cart Icon
export const FloatingCartIcon = () => {
  const { getCartCount, setIsCartOpen } = useCart();
  const count = getCartCount();

  return (
    <button
      onClick={() => setIsCartOpen(true)}
      className="fixed bottom-36 right-4 z-50 bg-amber-500 hover:bg-amber-600 text-white p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
      aria-label="Open cart"
    >
      <ShoppingBag className="w-6 h-6" />
      {count > 0 && (
        <span className="absolute -top-2 -right-2 bg-teal-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
};

// Cart Notification Toast
export const CartNotification = () => {
  const { notification } = useCart();
  
  if (!notification) return null;
  
  return (
    <div className="fixed top-20 right-4 z-50 animate-in slide-in-from-right duration-300">
      <div className="bg-teal-600 text-white px-4 py-3 rounded-lg shadow-lg flex items-center gap-2">
        <ShoppingBag className="w-5 h-5" />
        <span>{notification.message}</span>
      </div>
    </div>
  );
};

// Cart Drawer
export const CartDrawer = () => {
  const { 
    cartItems, 
    isCartOpen, 
    setIsCartOpen, 
    removeFromCart, 
    updateQuantity, 
    getCartTotal,
    getCartCount,
    checkout,
    clearCart
  } = useCart();

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={() => setIsCartOpen(false)}
      />
      
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-amber-500 to-teal-500 text-white">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-6 h-6" />
            <h2 className="text-lg font-bold">Your Cart ({getCartCount()})</h2>
          </div>
          <button 
            onClick={() => setIsCartOpen(false)}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4 max-h-[calc(100vh-280px)]">
          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Your cart is empty</p>
              <Button 
                onClick={() => setIsCartOpen(false)}
                variant="outline"
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
              >
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div 
                  key={item.id} 
                  className="flex gap-4 p-3 bg-gray-50 rounded-lg border border-gray-100"
                >
                  {/* Product Image */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-white flex-shrink-0">
                    <img 
                      src={item.image || 'https://via.placeholder.com/80'} 
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 text-sm line-clamp-2">{item.name}</h3>
                    <p className="text-amber-600 font-bold mt-1">${item.price?.toFixed(2)}</p>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-1 bg-white border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-1 bg-white border border-gray-200 rounded hover:bg-gray-100 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors ml-auto"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200 space-y-4">
            {/* Subtotal */}
            <div className="flex items-center justify-between text-lg">
              <span className="text-gray-600">Subtotal</span>
              <span className="font-bold text-gray-900">${getCartTotal().toFixed(2)}</span>
            </div>
            
            <p className="text-xs text-gray-500 text-center">
              Shipping & taxes calculated at checkout
            </p>
            
            {/* Checkout Button */}
            <Button 
              onClick={checkout}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg font-semibold"
            >
              <ExternalLink className="w-5 h-5 mr-2" />
              Checkout on Shopify
            </Button>
            
            {/* Clear Cart */}
            <button
              onClick={clearCart}
              className="w-full text-sm text-gray-500 hover:text-red-500 transition-colors py-2"
            >
              Clear Cart
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// Add to Cart Button Component
export const AddToCartButton = ({ product, variant = 'default', className = '' }) => {
  const { addToCart } = useCart();
  
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
  };
  
  if (variant === 'icon') {
    return (
      <button
        onClick={handleClick}
        className={`p-3 bg-teal-500 rounded-full shadow-lg hover:bg-teal-600 transition-colors ${className}`}
        title="Add to Cart"
      >
        <Plus className="w-5 h-5 text-white" />
      </button>
    );
  }
  
  if (variant === 'small') {
    return (
      <button
        onClick={handleClick}
        className={`flex items-center gap-1 px-3 py-1.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-md transition-colors ${className}`}
      >
        <Plus className="w-4 h-4" />
        Add
      </button>
    );
  }
  
  return (
    <Button
      onClick={handleClick}
      className={`bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white ${className}`}
    >
      <ShoppingBag className="w-4 h-4 mr-2" />
      Add to Cart
    </Button>
  );
};

export default CartDrawer;
